// Prospect schedules are calendar policy, never elapsed-time guesses. All
// choices must be confirmed before the database enables a live sequence.
export const REST_POLICIES = Object.freeze(['ACCUMULATED_BUSINESS_HOURS', 'NEXT_BUSINESS_DAY_SAME_TIME'])
export const WEEKEND_POLICIES = Object.freeze(['WEEKDAYS_ONLY', 'ALL_DAYS'])
const formatters = new Map()
export function localParts(instant, timezone) {
  if (!timezone || typeof timezone !== 'string') throw new Error('verified_timezone_required')
  let formatter = formatters.get(timezone)
  if (!formatter) {
    try { formatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', weekday: 'short' }) }
    catch { throw new Error('invalid_timezone') }
    formatters.set(timezone, formatter)
  }
  const date = new Date(instant)
  if (!Number.isFinite(date.getTime())) throw new Error('invalid_timestamp')
  const values = Object.fromEntries(formatter.formatToParts(date).filter(part => part.type !== 'literal').map(part => [part.type, part.value]))
  return { year: +values.year, month: +values.month, day: +values.day, hour: +values.hour, minute: +values.minute, second: +values.second, weekday: values.weekday, date: `${values.year}-${values.month}-${values.day}` }
}
function requirePolicy(policy) {
  if (!WEEKEND_POLICIES.includes(policy)) throw new Error('weekend_policy_confirmation_required')
}
export function isBusinessInstant(instant, timezone, weekendPolicy) {
  requirePolicy(weekendPolicy)
  const parts = localParts(instant, timezone)
  return (weekendPolicy === 'ALL_DAYS' || !['Sat', 'Sun'].includes(parts.weekday)) && parts.hour >= 9 && parts.hour < 18
}
// Calendar conversion uses the actual timezone offset; a missing DST wall time
// is rejected rather than silently shifted. Scheduling uses 09:00-18:00, away
// from the US DST transition gap. Ambiguous times use the earlier instant.
export function wallTimeToInstant(parts, timezone) {
  const nominal = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute || 0, parts.second || 0)
  let candidate = nominal
  for (let index = 0; index < 4; index++) {
    const actual = localParts(candidate, timezone)
    const wall = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second)
    candidate += nominal - wall
  }
  const target = [parts.year, parts.month, parts.day, parts.hour, parts.minute || 0, parts.second || 0].join(':')
  const same = value => { const p = localParts(value, timezone); return [p.year, p.month, p.day, p.hour, p.minute, p.second].join(':') === target }
  if (!same(candidate)) throw new Error('nonexistent_local_time')
  if (same(candidate - 3600000)) candidate -= 3600000
  return new Date(candidate).toISOString()
}
function nextCalendarDay(parts) {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + 1))
  return { ...parts, year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }
}
export function computeRestDue({ acceptedAt, timezone, timezoneVerified, restPolicy, weekendPolicy }) {
  if (timezoneVerified !== true) throw new Error('verified_timezone_required')
  requirePolicy(weekendPolicy)
  if (!REST_POLICIES.includes(restPolicy)) throw new Error('rest_policy_confirmation_required')
  let cursor = new Date(acceptedAt).getTime()
  if (!Number.isFinite(cursor)) throw new Error('invalid_timestamp')
  const initial = localParts(cursor, timezone)
  if (restPolicy === 'NEXT_BUSINESS_DAY_SAME_TIME') {
    let next = initial
    for (let count = 0; count < 8; count++) {
      next = nextCalendarDay(next)
      const result = wallTimeToInstant(next, timezone)
      if (weekendPolicy === 'ALL_DAYS' || !['Sat', 'Sun'].includes(localParts(result, timezone).weekday)) return result
    }
    throw new Error('calendar_window_unavailable')
  }
  let remaining = 24 * 3600000
  // Bounded minute slices preserve seconds and account for local DST/weekends.
  for (let count = 0; count < 20160; count++) {
    const p = localParts(cursor, timezone)
    const slice = Math.min(60000 - p.second * 1000 - (cursor % 1000), remaining)
    if (isBusinessInstant(cursor, timezone, weekendPolicy)) remaining -= slice
    cursor += slice
    if (remaining === 0) return new Date(cursor).toISOString()
  }
  throw new Error('calendar_window_unavailable')
}
export function planSequence({ now, timezone, timezoneVerified, weekendPolicy }) {
  if (timezoneVerified !== true) throw new Error('verified_timezone_required')
  requirePolicy(weekendPolicy)
  let day = localParts(now, timezone)
  // First touch is morning, prospect-local; no catch-up sends outside its slot.
  for (let count = 0; count < 10; count++) {
    const first = wallTimeToInstant({ ...day, hour: 10, minute: 0, second: 0 }, timezone)
    if (new Date(first) >= new Date(now) && isSubmissionWindow({instant:first,timezone,timezoneVerified,weekendPolicy,step:'FIRST'})) {
      try { return { firstDueAt: first, followupDueAt: planFollowup({ acceptedAt: first, timezone, timezoneVerified, weekendPolicy }) } }
      catch (error) { if (error.message !== 'followup_window_conflict') throw error }
    }
    day = nextCalendarDay(day)
  }
  throw new Error('calendar_window_unavailable')
}
export function isSubmissionWindow({ instant, timezone, timezoneVerified, weekendPolicy, step }) {
  if (timezoneVerified !== true || !isBusinessInstant(instant, timezone, weekendPolicy)) return false
  const parts = localParts(instant, timezone)
  // Email cadence is always weekday-only. The saved weekend policy still
  // governs the separate post-email rest calculation.
  if (['Sat', 'Sun'].includes(parts.weekday)) return false
  const hour = parts.hour
  return step === 'FIRST' ? hour < 12 : step === 'FOLLOWUP' ? hour >= 12 : false
}
export function validateSequenceSchedule({ now, firstDueAt, followupDueAt, timezone, timezoneVerified, weekendPolicy }) {
  if (timezoneVerified !== true) throw new Error('verified_timezone_required')
  requirePolicy(weekendPolicy)
  // Explicit offset-bearing timestamps avoid interpreting an operator's local
  // wall time as the server's timezone. Validation never changes either time.
  const explicit = value => typeof value === 'string' && /T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/i.test(value)
  if (!explicit(firstDueAt) || !explicit(followupDueAt)) throw new Error('explicit_schedule_required')
  const first = Date.parse(firstDueAt), followup = Date.parse(followupDueAt), current = new Date(now).getTime()
  if (![first, followup, current].every(Number.isFinite)) throw new Error('invalid_timestamp')
  if (first < current) throw new Error('schedule_in_past')
  if (!isSubmissionWindow({ instant: first, timezone, timezoneVerified, weekendPolicy, step: 'FIRST' }) || !isSubmissionWindow({ instant: followup, timezone, timezoneVerified, weekendPolicy, step: 'FOLLOWUP' })) throw new Error('schedule_outside_business_window')
  validateFollowupSchedule({acceptedAt:firstDueAt,followupDueAt,timezone,timezoneVerified,weekendPolicy})
  return { firstDueAt, followupDueAt }
}
export function validateFollowupSchedule({acceptedAt,followupDueAt,timezone,timezoneVerified,weekendPolicy}){
  if(timezoneVerified!==true)throw new Error('verified_timezone_required')
  requirePolicy(weekendPolicy)
  const first=Date.parse(acceptedAt),followup=Date.parse(followupDueAt)
  if(!Number.isFinite(first)||!Number.isFinite(followup))throw new Error('invalid_timestamp')
  if(!isSubmissionWindow({instant:followup,timezone,timezoneVerified,weekendPolicy,step:'FOLLOWUP'}))throw new Error('schedule_outside_business_window')
  if (['Sat','Sun'].includes(localParts(first,timezone).weekday)) throw new Error('schedule_outside_business_window')
  if (localParts(followup,timezone).date !== followupWindow({acceptedAt,timezone,timezoneVerified,weekendPolicy}).date) throw new Error('followup_window_conflict')
  return followupDueAt
}
export function followupWindow({ acceptedAt, timezone, timezoneVerified, weekendPolicy }) {
  if (timezoneVerified !== true) throw new Error('verified_timezone_required')
  requirePolicy(weekendPolicy)
  const start = new Date(acceptedAt).getTime()
  if (!Number.isFinite(start)) throw new Error('invalid_timestamp')
  let day = localParts(start,timezone), businessDays = 0
  for (let count = 0; count < 8; count++) {
    day = nextCalendarDay(day)
    const afternoon = wallTimeToInstant({...day,hour:12,minute:0,second:0},timezone)
    if (!['Sat','Sun'].includes(localParts(afternoon,timezone).weekday)) {
      businessDays++
      if (businessDays === 2) return {date:localParts(afternoon,timezone).date,startsAt:afternoon,expiresAt:wallTimeToInstant({...day,hour:18,minute:0,second:0},timezone)}
    }
  }
  throw new Error('calendar_window_unavailable')
}
export function planFollowup({ acceptedAt, timezone, timezoneVerified, weekendPolicy }) {
  const window = followupWindow({acceptedAt,timezone,timezoneVerified,weekendPolicy})
  if (['Sat','Sun'].includes(localParts(acceptedAt,timezone).weekday)) throw new Error('schedule_outside_business_window')
  return wallTimeToInstant({...localParts(window.startsAt,timezone),hour:13},timezone)
}
